import { useState } from 'react'
import { Loader2, LogOut, Monitor, RefreshCw, Smartphone } from 'lucide-react'
import { ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { EmptyState, ErrorStrip, LoadingRows, Note } from '@/shared/ui/states'
import { useLoad } from '@/features/administration/hooks'
import { explain } from '@/shared/api/problem'
import { api } from '@/shared/api/client'
import { fmtDateTime } from '@/shared/lib/format'
import { cn } from '@/lib/utils'
import { revokeOwnSession } from './api'
import type { SessionResponse } from '@/shared/api/types'

/**
 * Your own sessions. No elevated permission — every authenticated user sees this.
 *
 * Shown on its own to anyone without `session:read-any`, and reachable from the
 * admin console as the "just mine" view.
 */
export function MySessionsPanel() {
  const { data, error, loading, reload } = useLoad(
    () => api.get<SessionResponse[]>('/auth/sessions'), [],
  )
  const [busy, setBusy] = useState<string | null>(null)
  const [note, setNote] = useState<{ tone: 'good' | 'bad'; text: string } | null>(null)

  const rows = data ?? []

  async function revoke(s: SessionResponse) {
    if (s.current && !window.confirm('This signs you out of this browser. Continue?')) return
    setBusy(s.familyId)
    setNote(null)
    try {
      await revokeOwnSession(s.familyId)
      if (s.current) { window.location.assign('/login'); return }
      setNote({ tone: 'good', text: 'Session revoked.' })
      reload()
    } catch (err) {
      setNote({ tone: 'bad', text: explain(err) })
    } finally {
      setBusy(null)
    }
  }

  async function revokeAll() {
    if (!window.confirm('Sign out of every device, including this one?')) return
    setBusy('all')
    try {
      await api.post('/auth/logout-all')
      window.location.assign('/login')
    } catch (err) {
      setNote({ tone: 'bad', text: explain(err) })
      setBusy(null)
    }
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="My sessions"
        description="Every device currently signed in as you. Revoking is immediate."
        right={
          <>
            <button className="erp-btn" onClick={reload} aria-label="Refresh">
              <RefreshCw className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Refresh</span>
            </button>
            {rows.length > 1 && (
              <button className="erp-btn" onClick={revokeAll} disabled={busy !== null}>
                {busy === 'all' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogOut className="h-3.5 w-3.5" />}
                Sign out everywhere
              </button>
            )}
          </>
        }
      />

      {note && <Note tone={note.tone} onDismiss={() => setNote(null)}>{note.text}</Note>}

      <Panel bodyClassName="p-0">
        <ErrorStrip message={error} />
        <ErpTable
          head={
            <>
              <Th>Device</Th>
              <Th className="axis-hide-sm">IP</Th>
              <Th className="axis-hide-sm">Started</Th>
              <Th className="axis-hide-sm">Expires</Th>
              <Th />
            </>
          }
        >
          {loading && rows.length === 0 ? (
            <LoadingRows colSpan={5} />
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={5}>
                <EmptyState icon={<Monitor className="h-4 w-4" />} title="No active sessions" />
              </td>
            </tr>
          ) : (
            rows.map((s) => (
              <tr key={s.familyId} className={cn('hover:bg-muted/40', s.current && 'bg-chocolate-soft/30')}>
                <Td>
                  <div className="flex items-center gap-1.5 min-w-0">
                    {/^.*(iphone|android|mobile).*$/i.test(s.userAgent ?? '')
                      ? <Smartphone className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      : <Monitor className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                    <span className="truncate max-w-[20rem]" title={s.userAgent ?? ''}>
                      {s.deviceLabel ?? 'Unknown device'}
                    </span>
                    {s.current && (
                      <span className="shrink-0 text-erp-xs px-1.5 py-0.5 rounded-md border border-chocolate/30 bg-chocolate-soft/50 text-chocolate font-medium">
                        This device
                      </span>
                    )}
                  </div>
                </Td>
                <Td className="axis-hide-sm font-mono text-erp-xs">{s.ip ?? '—'}</Td>
                <Td className="axis-hide-sm whitespace-nowrap erp-metric">{fmtDateTime(s.issuedAt)}</Td>
                <Td className="axis-hide-sm whitespace-nowrap erp-metric">{fmtDateTime(s.expiresAt)}</Td>
                <Td className="text-right">
                  <button className="erp-btn" disabled={busy !== null} onClick={() => revoke(s)}>
                    {busy === s.familyId
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <LogOut className="h-3.5 w-3.5" />}
                    <span className="axis-hide-sm">Revoke</span>
                  </button>
                </Td>
              </tr>
            ))
          )}
        </ErpTable>
      </Panel>
    </div>
  )
}
